import type {
  LoginData,
  LoginResponse,
  RegisterData,
  RegisterResponse,
  FanbasisRegisterData,
  ForgotPasswordData,
  ResetPasswordData,
  ChangePasswordData,
  ApiResponse,
  User,
} from "./types/auth";
import type { AdBreak } from "./types/ads";

// Re-export ApiResponse and AdBreak for use in other modules
export type { ApiResponse, AdBreak };

// Custom field definition from backend
export interface CustomField {
  id: number;
  name: string;
  slug: string;
  label: string;
  type: "text" | "number" | "select" | "textarea" | "image" | "date";
  placeholder?: string;
  tooltip?: string;
  is_required: number; // 1 = required, 0 = optional
  is_hidden: number;
  visible_on_form: number;
  options?: string[]; // For select fields
  default_option?: string | null;
  order?: number | null;
  validation_rules?: string[] | null;
  sanitization?: string[] | null;
  max_size?: number | null;
}

// Custom field value stored on a listing
export interface CustomFieldValue {
  id: number;
  listing_id: string;
  custom_field_id: number;
  value: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface ListingCategory {
  id: number;
  name: string;
  description: string | null;
  allow_guest_to_view: number;
  custom_fields?: CustomField[];
}

export interface Listing {
  id: string;
  name: string;
  code: string;
  category_id: number;
  user_id: string;
  user_avatar: string | null;
  status: string;
  free_access: number;
  category?: ListingCategory;
  custom_fields?: CustomField[];
  custom_field_values?: CustomFieldValue[];
  created_at: string;
  updated_at?: string;
  /** YouTube-style ad breaks for this listing */
  ad_breaks?: AdBreak[];
}

// Payload for initial listing creation (step 1)
export interface CreateListingPayload {
  name?: string; // Optional name for the listing
  category_id: number;
  status: string;
  free_access?: boolean; // Whether listing has free access
  custom_fields: number[]; // Array of field IDs to attach
}

// Payload for V2 listing creation (single-step with field values)
export interface CreateListingV2Payload {
  category_id: number;
  status: string;
  free_access?: boolean;
  membership_plan_id?: number;
  custom_fields: Array<{
    custom_field_id: number;
    value?: string | File | null;
  }>;
}

// Payload for adding/updating field values (step 2)
export interface CreateListingFieldValuesPayload {
  listing_id: string;
  custom_fields: Array<{
    custom_field_id: number;
    value?: string | File | null;
  }>;
}

export interface UpdateListingFieldValuesPayload {
  custom_fields: Array<{
    custom_field_id: number;
    value?: string | File | null;
  }>;
}

export interface CreateListingResponse {
  message: string;
  listing: Listing;
}

export interface GetUserListingsResponse {
  status: string;
  message: string;
  listings: Listing[];
  metadata: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

export interface UpdateListingResponse {
  message: string;
  listing: Listing;
}

export interface DeleteListingResponse {
  message: string;
}

export interface CategoryCustomFieldsResponse {
  message: string;
  category: Array<{
    id: number;
    name: string;
    description: string;
    allow_guest_to_view: number;
    custom_fields: CustomField[];
  }>;
}

const AUTH_TOKEN_KEY = "app_auth_token";

// Token management utilities
export const authToken = {
  get: (): string | null => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(AUTH_TOKEN_KEY);
  },
  set: (token: string): void => {
    if (typeof window === "undefined") return;
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  },
  clear: (): void => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(AUTH_TOKEN_KEY);
  },
};

/**
 * Construct API URL using the secure Next.js proxy route
 * All requests go through /api/proxy/{endpoint} which securely forwards
 * to the Laravel backend with the FRONTEND_KEY (server-side only)
 */
const getApiUrl = (endpoint: string): string => {
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
  // Route through Next.js proxy (adds FRONTEND_KEY server-side)
  return `/api/proxy/${cleanEndpoint}`;
};

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "Accept": "application/json",
    };

    // Note: X-Frontend-Key is added server-side by the proxy route
    // It's never exposed to the browser - only the JWT token is sent from client

    // Add JWT token for authenticated requests
    const token = authToken.get();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    // Request goes to Next.js proxy route, which forwards to Laravel backend
    const response = await fetch(getApiUrl(endpoint), {
      ...options,
      method: options.method || "GET",
      headers,
      credentials: "include",
      cache: "no-store", // Disable fetch caching to ensure fresh data
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      // Log the full error response for debugging
      console.error(`[API] Error response for ${endpoint}:`, {
        status: response.status,
        statusText: response.statusText,
        data: data,
      });
      
      return {
        success: false,
        error: data.message || data.error || data.detail || response.statusText || "An error occurred",
        errors: data.errors || undefined,
        status: response.status,
        data: data, // Include full response data for debugging
      };
    }

    return {
      success: true,
      data: data,
      message: data.message,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Network error occurred",
    };
  }
}

// Auth API methods
export const authApi = {
  // POST /login - Authenticate user
  login: async (data: LoginData) => {
    const response = await apiRequest<LoginResponse>("/login", {
      method: "POST",
      body: JSON.stringify(data),
    });

    // Auto-store token on successful login
    if (response.success && response.data?.token) {
      authToken.set(response.data.token);
    }

    return response;
  },

  // POST /logout - Invalidate session
  logout: async () => {
    const response = await apiRequest("/logout", {
      method: "POST",
    });

    // Always clear token on logout
    authToken.clear();
    return response;
  },

  // GET /user - Get current authenticated user
  // Note: Backend returns User object directly, not wrapped in { user: ... }
  // See Swagger spec: GET /api/user returns User schema directly (api-docs.json)
  getCurrentUser: async () => {
    return apiRequest<User>("/user", {
      method: "GET",
    });
  },

  // POST /register - Create new user
  register: async (data: RegisterData) => {
    const response = await apiRequest<RegisterResponse>("/register", {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (response.success && response.data?.token) {
      authToken.set(response.data.token);
    }

    return response;
  },

  // POST /fanbasis-register - Token-based registration for Fanbasis buyers
  fanbasisRegister: async (data: FanbasisRegisterData) => {
    const response = await apiRequest<RegisterResponse>("/fanbasis-register", {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (response.success && response.data?.token) {
      authToken.set(response.data.token);
    }

    return response;
  },

  // POST /forgot-password - Request password reset email
  forgotPassword: async (data: ForgotPasswordData) => {
    return apiRequest("/forgot-password", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // POST /password-reset - Reset password with token
  resetPassword: async (data: ResetPasswordData) => {
    return apiRequest("/password-reset", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // POST /user-change-password - Change password for authenticated user
  changePassword: async (data: ChangePasswordData) => {
    return apiRequest<{ message: string }>("/user-change-password", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
};

// Helper for FormData requests
async function apiRequestFormData<T>(
  endpoint: string,
  formData: FormData,
  method: string
): Promise<ApiResponse<T>> {
  try {
    const headers: Record<string, string> = {
      "Accept": "application/json",
    };

    // Add JWT token for authenticated requests
    const token = authToken.get();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(getApiUrl(endpoint), {
      method,
      body: formData,
      headers,
      credentials: "include",
      cache: "no-store", // Disable fetch caching to ensure fresh data
      // Note: Don't set Content-Type header - browser will set it with boundary
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      return {
        success: false,
        error: data.message || data.error || "Request failed",
        errors: data.errors || undefined,
        status: response.status,
      };
    }

    return {
      success: true,
      data: data,
      message: data.message,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Network error",
    };
  }
}

// Helper to convert field values to FormData for file uploads
const createFieldValuesFormData = (
  data: UpdateListingFieldValuesPayload | CreateListingFieldValuesPayload,
  listingId?: string
): FormData => {
  const formData = new FormData();

  if (listingId) {
    formData.append("listing_id", listingId);
    console.log(`[API] Adding listing_id to FormData: ${listingId}`);
  }

  console.log(`[API] Creating FormData for ${data.custom_fields.length} custom fields`);
  
  data.custom_fields.forEach((field, index) => {
    const fieldId = field.custom_field_id;
    console.log(`[API] Field index ${index}: custom_field_id=${fieldId}, value=${field.value}`);
    formData.append(`custom_fields[${index}][custom_field_id]`, String(fieldId));

    if (field.value instanceof File) {
      formData.append(`custom_fields[${index}][value]`, field.value);
      console.log(`[API] Field index ${index}: appended File`);
    } else if (field.value !== null && field.value !== undefined) {
      // Append the value - even if empty string (momscompas pattern)
      formData.append(`custom_fields[${index}][value]`, String(field.value));
      console.log(`[API] Field index ${index}: appended value="${String(field.value)}"`);
    } else {
      // Null/empty value - append empty string (momscompas pattern)
      formData.append(`custom_fields[${index}][value]`, '');
      console.log(`[API] Field index ${index}: appended empty string`);
    }
  });

  return formData;
};

export const listingApi = {
  // Get current user's listings with pagination
  getUserListings: async (page: number = 1, perPage: number = 100) => {
    return apiRequest<GetUserListingsResponse>(`/user-listings?page=${page}&per_page=${perPage}`, {
      method: "GET",
    });
  },

  // Get ALL user's listings by fetching all pages
  getAllUserListings: async (): Promise<ApiResponse<GetUserListingsResponse>> => {
    const allListings: Listing[] = [];
    let currentPage = 1;
    let lastPage: number;
    let responseData: GetUserListingsResponse | null = null;

    // Fetch all pages
    do {
      const response = await listingApi.getUserListings(currentPage, 100);
      if (!response.success || !response.data) {
        return response;
      }
      
      allListings.push(...response.data.listings);
      lastPage = response.data.metadata.last_page;
      responseData = response.data;
      currentPage++;
    } while (currentPage <= lastPage);

    // Return combined response with all listings
    return {
      success: true,
      data: {
        ...responseData!,
        listings: allListings,
        metadata: {
          ...responseData!.metadata,
          current_page: 1,
          // Preserve original per-page limit instead of using aggregated length
          per_page: responseData!.metadata.per_page,
          // Preserve original total count from API metadata
          total: responseData!.metadata.total,
          last_page: 1,
        },
      },
    };
  },

  // Get custom fields for a category
  getCategoryCustomFields: async (categoryId: number) => {
    return apiRequest<CategoryCustomFieldsResponse>(`/listings/category-custom-fields?category_id=${categoryId}`, {
      method: "GET",
    });
  },

  // Get a single listing by ID
  getListing: async (listingId: string) => {
    return apiRequest<{ status: string; message: string; listing: Listing }>(`/listings/${listingId}`, {
      method: "GET",
    });
  },

  // Step 1: Create listing structure (V1 - deprecated, use createListingV2)
  createListing: async (data: CreateListingPayload) => {
    // Ensure free_access is sent as a proper boolean for Laravel validation
    // Default to true if not explicitly specified
    const payload = {
      ...data,
      free_access: data.free_access ?? true,
    };
    return apiRequest<CreateListingResponse>("/listings", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  // V2: Create listing with field values in one request
  createListingV2: async (data: CreateListingV2Payload) => {
    const formData = new FormData();
    
    // Add category_id and status FIRST (required fields)
    formData.append("category_id", String(data.category_id));
    formData.append("status", data.status);
    
    // Add free_access - default to true (1) if not explicitly specified
    const freeAccess = data.free_access ?? true;
    formData.append("free_access", freeAccess ? "1" : "0");
    if (data.membership_plan_id) {
      formData.append("membership_plan_id", String(data.membership_plan_id));
    }
    
    // Add custom fields
    data.custom_fields.forEach((field, index) => {
      formData.append(`custom_fields[${index}][custom_field_id]`, String(field.custom_field_id));

      if (field.value instanceof File) {
        formData.append(`custom_fields[${index}][value]`, field.value);
      } else if (field.value !== null && field.value !== undefined && field.value !== "") {
        // Only append non-empty string values
        formData.append(`custom_fields[${index}][value]`, String(field.value));
      }
    });
    
    return apiRequestFormData<CreateListingResponse>("/listingsV2", formData, "POST");
  },

  // Step 2: Add field values to a listing (uses FormData for file uploads)
  addListingFieldValues: async (data: CreateListingFieldValuesPayload) => {
    const formData = createFieldValuesFormData(data, data.listing_id);
    return apiRequestFormData<{ message: string; data: unknown }>("/listings/field-values", formData, "POST");
  },

  // Update field values (POST, uses FormData for file uploads)
  updateListingFieldValues: async (listingId: string, data: UpdateListingFieldValuesPayload) => {
    // Don't pass listingId to FormData for update - it's only in the URL
    const formData = createFieldValuesFormData(data);
    return apiRequestFormData<UpdateListingResponse>(`/listings/field-values-update/${listingId}`, formData, "POST");
  },

  // Delete a listing
  deleteListing: async (listingId: string) => {
    return apiRequest<DeleteListingResponse>(`/listings/${listingId}`, {
      method: "DELETE",
    });
  },

  // Update listing status (active/inactive)
  updateListingStatus: async (listingId: string, status: string) => {
    return apiRequest<{ message: string; listing: Listing }>(`/listings/${listingId}`, {
      method: "PUT",
      body: JSON.stringify({ status }),
    });
  },
};

// Video API Types
export interface Video {
  id: number;
  user_id: string;
  listing_id?: string;
  step_number?: number;
  mediacms_id?: string;
  title: string;
  description?: string;
  thumbnail_url?: string;
  thumbnail_custom?: string;
  thumbnail_auto?: string;
  video_url?: string;
  hls_path?: string;
  hls_playlist_url?: string;
  raw_path?: string;
  storage_disk?: string;
  status: "pending" | "processing" | "completed" | "failed" | "ready";
  settings?: {
    is_public?: boolean;
    enable_comments?: boolean;
  };
  file_size?: number;
  duration?: number;
  views_count?: number;
  available_qualities?: string[];
  created_at: string;
  updated_at: string;
}

export interface CreateUploadUrlPayload {
  filename: string;
  content_type?: string;
  is_public?: boolean;
}

export interface CreateUploadUrlResponse {
  upload_url: string;
  object_path: string;
  bucket_type: "public" | "private";
}

export interface CompleteVideoUploadPayload {
  object_path: string;
  title: string;
  description?: string;
  listing_id?: string;
  step_number?: number;
}

export interface GetVideosResponse {
  data: Video[];
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

// Video API
export const videoApi = {
  // Create presigned upload URL for direct MinIO upload
  createUploadUrl: async (payload: CreateUploadUrlPayload) => {
    return apiRequest<CreateUploadUrlResponse>("/videos/create-upload-url", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  // Upload video file directly to MinIO using presigned URL
  uploadToMinIO: async (uploadUrl: string, file: File, onProgress?: (progress: number) => void) => {
    return new Promise<boolean>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            onProgress(percentComplete);
          }
        });
      }

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Ensure progress is set to 100% on successful completion
          // (XHR progress events may not always reach exactly 100%)
          if (onProgress) {
            onProgress(100);
          }
          resolve(true);
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Network error during upload"));
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("Upload aborted by user"));
      });

      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);
    });
  },

  // Mark video upload as complete and start processing
  completeUpload: async (payload: CompleteVideoUploadPayload) => {
    return apiRequest<Video>("/videos/complete", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  // Get user's videos
  getVideos: async (page: number = 1, perPage: number = 15) => {
    return apiRequest<GetVideosResponse>(`/videos?page=${page}&per_page=${perPage}`, {
      method: "GET",
    });
  },

  // Get video by ID
  getVideo: async (videoId: number) => {
    return apiRequest<Video>(`/videos/${videoId}`, {
      method: "GET",
    });
  },

  // Update video metadata
  updateVideo: async (videoId: number, data: { title?: string; description?: string; is_public?: boolean; enable_comments?: boolean }) => {
    return apiRequest<Video>(`/videos/${videoId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // Delete video
  deleteVideo: async (videoId: number) => {
    return apiRequest<void>(`/videos/${videoId}`, {
      method: "DELETE",
    });
  },

  // Upload custom thumbnail for video
  uploadThumbnail: async (videoId: number, thumbnailFile: File) => {
    const formData = new FormData();
    formData.append("thumbnail", thumbnailFile);

    return apiRequestFormData<{ message: string; thumbnail_url: string }>(
      `/videos/${videoId}/thumbnail`,
      formData,
      "POST"
    );
  },

  // Get signed URL for video playback and thumbnail
  getSignedUrl: async (videoId: number, expiresInMinutes: number = 60) => {
    return apiRequest<{
      signed_url: string;
      expires_at: string;
      thumbnail_signed_url?: string;
      hls_playlist_url?: string;
    }>(
      `/videos/${videoId}/signed-url?expires=${expiresInMinutes}`,
      {
        method: "GET",
      }
    );
  },

  // Get playback URLs (dynamic endpoint with fresh signed URLs for HLS streaming)
  getPlaybackUrls: async (videoId: number) => {
    try {
      const response = await apiRequest<{
        video_id: number;
        status: string;
        duration: number;
        available_qualities: string[];
        thumbnail_url: string;
        master_playlist_url?: string;
        quality_playlists?: Record<string, string>;
        video_url?: string;
        expires_at: string;
      }>(
        `/videos/${videoId}/playback-urls`,
        {
          method: "GET",
        }
      );

      // Transform Laravel backend URLs to Next.js proxy URLs
      if (response.success && response.data) {
        const data = response.data;

        // Helper function to convert Laravel backend URL to Next.js proxy URL
        const toProxyUrl = (url: string | undefined): string | undefined => {
          if (!url) return undefined;

          // Extract the path after /api/ from the Laravel URL
          const apiMatch = url.match(/\/api\/(.+)$/);
          if (apiMatch) {
            return `/api/proxy/${apiMatch[1]}`;
          }

          return url;
        };

        // Convert URLs
        if (data.master_playlist_url) {
          data.master_playlist_url = toProxyUrl(data.master_playlist_url);
        }

        if (data.quality_playlists) {
          const convertedPlaylists: Record<string, string> = {};
          for (const [quality, url] of Object.entries(data.quality_playlists)) {
            convertedPlaylists[quality] = toProxyUrl(url) || url;
          }
          data.quality_playlists = convertedPlaylists;
        }

        if (data.video_url) {
          data.video_url = toProxyUrl(data.video_url);
        }
      }

      return response;
    } catch (error) {
      console.error("Error in getPlaybackUrls:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to get playback URLs",
      };
    }
  },
};

// Wishlist API Types
export interface WishlistItem {
  id: number;
  listing_id: string;
  domain: string;
  created_at: string;
}

export interface WishlistResponse {
  data: WishlistItem[];
}

export interface SaveWishlistResponse {
  message: string;
  created: WishlistItem[];
  skipped: string[];
}

// Wishlist API
export const wishlistApi = {
  // Get current user's wishlist
  getWishlist: async () => {
    return apiRequest<WishlistResponse>("/current-user-wishlist", {
      method: "GET",
    });
  },

  // Add item to wishlist
  addToWishlist: async (listingId: string) => {
    return apiRequest<SaveWishlistResponse>("/save-to-wishlist", {
      method: "POST",
      body: JSON.stringify({
        items: [{ listing_id: listingId }],
      }),
    });
  },

  // Remove item from wishlist
  removeFromWishlist: async (listingId: string) => {
    return apiRequest<{ message: string }>(`/remove-from-wishlist/${listingId}`, {
      method: "DELETE",
    });
  },

  // Check if listing is in wishlist
  isInWishlist: async (listingId: string): Promise<boolean> => {
    const response = await wishlistApi.getWishlist();
    if (response.success && response.data?.data) {
      return response.data.data.some(item => item.listing_id === listingId);
    }
    return false;
  },
};
